/**
 * F38. KPIs Financieros con semáforo.
 *
 * Selector de año + periodo (YEAR / Q1..Q4 / MONTH). Para MONTH se elige el
 * mes (1-12).
 *
 * Renderiza cuatro secciones:
 *   1. Scorecards de ratios de balance y P&L con código de color (verde /
 *      amarillo / rojo / gris N/A) y tooltip con fórmula, umbrales y valor.
 *      Click en una card abre un drawer lateral con las cuentas del PGC que
 *      componen el numerador y el denominador.
 *   2. Estructura del balance: dos barras horizontales apiladas al 100%
 *      (AC+ANC frente a PC+PNC+PN) usando Recharts.
 *   3. Waterfall del P&L: gráfico de cascada ING → CMV → … → Beneficio Neto.
 *      Implementado con dos series (base invisible + delta visible) en
 *      Recharts BarChart.
 *   4. Análisis vertical: tabla con cada concepto, su importe y su % sobre
 *      ingresos.
 *
 * ADMIN-only (la API valida el rol).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ratioInsightKey, type RatioKey } from '../lib/ratio-insight'
import { AlertCircle, Info, Loader2, TrendingUp } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import type {
  AccountRow,
  FinancialKpisResponse,
  Ratio,
  Semaforo,
  WaterfallStep,
} from '@toga/shared'
import KpiDrilldownDrawer from '../components/accounting/KpiDrilldownDrawer'

// ============================================================================
// Constantes
// ============================================================================

const CURRENT_YEAR = new Date().getFullYear()

type PeriodMode = 'YEAR' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'MONTH'

/** Granularidad del control segmentado. */
/**
 * Pestañas, como en Ledgeria. Separan tres preguntas distintas que antes
 * compartían un scroll larguísimo: cómo está la cosa AHORA (cuadro de mando),
 * el detalle en cifras (tablas), y cómo evoluciona (comparativa anual).
 */
type Tab = 'DASHBOARD' | 'TABLES' | 'COMPARISON'
const TABS: Tab[] = ['DASHBOARD', 'TABLES', 'COMPARISON']

type Granularity = 'YEAR' | 'QUARTER' | 'MONTH'
const GRANULARITIES: Granularity[] = ['YEAR', 'QUARTER', 'MONTH']

/** Trimestre natural de hoy, para no aterrizar siempre en T1 al cambiar de pestaña. */
function currentQuarterOf(previous: PeriodMode): PeriodMode {
  if (previous.startsWith('Q')) return previous
  const q = Math.floor(new Date().getMonth() / 3) + 1
  return `Q${q}` as PeriodMode
}

const PERIOD_OPTIONS: Array<{ value: PeriodMode; label: string }> = [
  { value: 'YEAR', label: 'Año completo' },
  { value: 'Q1', label: 'T1 (ene–mar)' },
  { value: 'Q2', label: 'T2 (abr–jun)' },
  { value: 'Q3', label: 'T3 (jul–sep)' },
  { value: 'Q4', label: 'T4 (oct–dic)' },
  { value: 'MONTH', label: 'Mes' },
]

const MONTH_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
]

// Colores semáforo
const STATUS_COLOR: Record<Semaforo, string> = {
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  na: '#64748b',
}

// Colores estructura balance
const BAL_AC = '#22d3ee' // cyan-400
const BAL_ANC = '#0891b2' // cyan-600
const BAL_PC = '#dc2626' // red-600
const BAL_PNC = '#991b1b' // red-800
const BAL_PN = '#16a34a' // emerald-600

// Colores waterfall
const WF_SUBTOTAL = '#2563eb' // blue-600 (saturado)
const WF_POSITIVE = '#16a34a' // emerald-600
const WF_NEGATIVE = '#dc262680' // red translúcido (50%)

// ============================================================================
// Formato de ratios
// ============================================================================

type RatioFormat = 'ratio' | 'pct' | 'cents'

function formatRatio(value: number | null, format: RatioFormat): string {
  if (value === null) return 'N/A'
  switch (format) {
    case 'ratio':
      return value.toFixed(2)
    case 'pct':
      return `${value.toFixed(1)}%`
    case 'cents':
      return formatCents(value)
  }
}

// ============================================================================
// Componente principal
// ============================================================================

export default function AccountingKpis() {
  const { t } = useTranslation('accounting')
  const PERIOD_OPTIONS_I18N: Array<{ value: PeriodMode; label: string }> = [
    { value: 'YEAR', label: t('period.year') },
    { value: 'Q1', label: t('period.q1') },
    { value: 'Q2', label: t('period.q2') },
    { value: 'Q3', label: t('period.q3') },
    { value: 'Q4', label: t('period.q4') },
    { value: 'MONTH', label: t('period.month') },
  ]
  const MONTH_OPTIONS_I18N: Array<{ value: number; label: string }> = [
    { value: 1, label: t('months.jan') },
    { value: 2, label: t('months.feb') },
    { value: 3, label: t('months.mar') },
    { value: 4, label: t('months.apr') },
    { value: 5, label: t('months.may') },
    { value: 6, label: t('months.jun') },
    { value: 7, label: t('months.jul') },
    { value: 8, label: t('months.aug') },
    { value: 9, label: t('months.sep') },
    { value: 10, label: t('months.oct') },
    { value: 11, label: t('months.nov') },
    { value: 12, label: t('months.dec') },
  ]
  void PERIOD_OPTIONS
  void MONTH_OPTIONS
  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [period, setPeriod] = useState<PeriodMode>('YEAR')
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1)
  const [tab, setTab] = useState<Tab>('DASHBOARD')

  // La granularidad NO es estado aparte: se deduce del periodo. Con dos estados
  // podrían contradecirse (pestaña "Mes" con period='Q3') y habría que
  // sincronizarlos a mano en cada cambio.
  const granularity: Granularity =
    period === 'YEAR' ? 'YEAR' : period === 'MONTH' ? 'MONTH' : 'QUARTER'

  const [data, setData] = useState<FinancialKpisResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTitle, setDrawerTitle] = useState('')
  const [drawerAccounts, setDrawerAccounts] = useState<AccountRow[]>([])

  const yearOptions = useMemo(() => {
    const arr: number[] = []
    for (let y = CURRENT_YEAR + 1; y >= CURRENT_YEAR - 6; y--) arr.push(y)
    return arr
  }, [])

  const load = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({
          year: String(year),
          period,
        })
        if (period === 'MONTH') params.set('month', String(month))
        const res = await api.get<FinancialKpisResponse>(
          `/accounting/kpis?${params.toString()}`
        )
        if (signal.aborted) return
        setData(res)
      } catch (err) {
        if (signal.aborted) return
        const text =
          err instanceof ApiError
            ? ((err.body as { error?: string })?.error ?? err.message)
            : err instanceof Error
              ? err.message
              : t('errorLoad')
        setError(text)
        setData(null)
      } finally {
        if (!signal.aborted) setLoading(false)
      }
    },
    [year, period, month, t]
  )

  useEffect(() => {
    const ctrl = new AbortController()
    void load(ctrl.signal)
    return () => ctrl.abort()
  }, [load])

  const openDrilldown = useCallback(
    (title: string, accounts: AccountRow[]) => {
      setDrawerTitle(title)
      setDrawerAccounts(accounts)
      setDrawerOpen(true)
    },
    []
  )

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-toga-500/15 text-toga-700 dark:text-toga-300">
          <TrendingUp size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold">{t('title')}</h1>
          <p className="text-sm t-muted">{t('subtitle')}</p>
        </div>
      </header>

      {/* Pestañas */}
      <div className="flex gap-6 border-b border-[var(--border)]">
        {TABS.map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            className={[
              'py-2.5 text-sm border-b-2 -mb-px transition-colors',
              tb === tab
                ? 'border-toga-500 t-primary font-semibold'
                : 'border-transparent t-muted hover:t-primary',
            ].join(' ')}
          >
            {t(`tabs.${tb.toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* Filtros — control segmentado Año/Trimestre/Mes, como en Ledgeria.
          Antes todo colgaba de un único desplegable que mezclaba "Año completo",
          los cuatro trimestres y "Mes": había que abrirlo para saber qué se
          estaba mirando. Separar la GRANULARIDAD (siempre visible) de la
          selección concreta hace que el estado se lea de un vistazo. */}
      {tab !== 'COMPARISON' && (
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('granularity')}</label>
          <div
            role="group"
            className="inline-flex rounded-lg border border-[var(--border)] bg-[var(--bg-input)] p-0.5"
          >
            {GRANULARITIES.map((g) => {
              const active = granularity === g
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => {
                    // Al cambiar de granularidad se elige un valor por defecto
                    // razonable: el trimestre o el mes en curso, no el primero.
                    if (g === 'YEAR') setPeriod('YEAR')
                    else if (g === 'QUARTER') setPeriod(currentQuarterOf(period))
                    else setPeriod('MONTH')
                  }}
                  className={[
                    'px-3 py-1.5 rounded-md text-sm transition',
                    active ? 'bg-toga-600 text-white' : 't-secondary hover:bg-hover',
                  ].join(' ')}
                >
                  {t(`granularityOption.${g.toLowerCase()}`)}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('year')}</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {granularity === 'QUARTER' && (
          <div>
            <label className="block text-xs font-medium t-muted mb-1">{t('quarterLabel')}</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as PeriodMode)}
              className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
            >
              {PERIOD_OPTIONS_I18N.filter((p) => p.value.startsWith('Q')).map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {granularity === 'MONTH' && (
          <div>
            <label className="block text-xs font-medium t-muted mb-1">{t('monthLabel')}</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
            >
              {MONTH_OPTIONS_I18N.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {data?.period.label && (
          <div className="ml-auto text-xs t-muted">
            {t('periodPrefix')} <span className="t-secondary">{data.period.label}</span>
          </div>
        )}
      </div>
      )}

      {tab !== 'COMPARISON' && error && (
        <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {tab !== 'COMPARISON' && loading && (
        <div className="flex items-center gap-2 text-sm t-muted">
          <Loader2 size={16} className="animate-spin" />
          {t('loading')}
        </div>
      )}

      {data && !loading && tab === 'DASHBOARD' && (
        <>
          <ScorecardsSection data={data} onDrilldown={openDrilldown} />
          <BalanceStructureSection vars={data.balance.vars} />
          <WaterfallSection steps={data.pnl.waterfall} />
        </>
      )}

      {data && !loading && tab === 'TABLES' && (
        <VerticalAnalysisSection rows={data.pnl.verticalAnalysis} />
      )}

      {tab === 'COMPARISON' && <YearComparisonSection years={yearOptions} />}

      <KpiDrilldownDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={drawerTitle}
        accounts={drawerAccounts}
      />
    </div>
  )
}

// ============================================================================
// Sección 1 — Scorecards
// ============================================================================

interface KpiCardDef {
  key: string
  ratio: Ratio
  format: RatioFormat
  /** Variables del PGC que componen el ratio (claves de drilldown). */
  drilldownKeys: string[]
}

function ScorecardsSection({
  data,
  onDrilldown,
}: {
  data: FinancialKpisResponse
  onDrilldown: (title: string, accounts: AccountRow[]) => void
}) {
  const { t } = useTranslation('accounting')
  const balanceCards: KpiCardDef[] = [
    {
      key: 'liquidezCorriente',
      ratio: data.balance.ratios.liquidezCorriente,
      format: 'ratio',
      drilldownKeys: ['AC', 'PC'],
    },
    // La prueba ácida es (AC − Existencias) / PC. En una empresa SIN existencias
    // —una consultora no tiene stock— el numerador es idéntico al de la liquidez
    // corriente, así que la tarjeta repite el mismo número. Y encima con otro
    // semáforo (verde 0,9–1,2 frente a 1,5–2,2), de modo que el MISMO valor podía
    // salir en verde arriba y en rojo justo al lado, que es alarmante y falso.
    // Se muestra solo si de verdad hay existencias que descontar.
    ...(data.balance.vars.EX !== 0
      ? [
          {
            key: 'pruebaAcida',
            ratio: data.balance.ratios.pruebaAcida,
            format: 'ratio' as const,
            drilldownKeys: ['AC', 'EX', 'PC'],
          },
        ]
      : []),
    {
      key: 'fondoManiobra',
      ratio: data.balance.ratios.fondoManiobra,
      format: 'cents',
      drilldownKeys: ['AC', 'PC'],
    },
    {
      key: 'endeudamiento',
      ratio: data.balance.ratios.endeudamiento,
      format: 'ratio',
      drilldownKeys: ['PC', 'PNC', 'PN'],
    },
    {
      key: 'calidadDeuda',
      ratio: data.balance.ratios.calidadDeuda,
      format: 'ratio',
      drilldownKeys: ['PC', 'PNC'],
    },
  ]

  const pnlCards: KpiCardDef[] = [
    {
      key: 'margenBrutoPct',
      ratio: data.pnl.ratios.margenBrutoPct,
      format: 'pct',
      drilldownKeys: ['ING', 'CMV'],
    },
    {
      key: 'ebitdaMarginPct',
      ratio: data.pnl.ratios.ebitdaMarginPct,
      format: 'pct',
      drilldownKeys: ['ING', 'CMV', 'GP', 'GOP'],
    },
    {
      key: 'netMarginPct',
      ratio: data.pnl.ratios.netMarginPct,
      format: 'pct',
      drilldownKeys: ['ING', 'CMV', 'GP', 'GOP', 'DA', 'GFI', 'IMP'],
    },
    {
      key: 'salesGrowthMoM',
      ratio: data.pnl.ratios.salesGrowthMoM,
      format: 'pct',
      drilldownKeys: ['ING'],
    },
    {
      key: 'salesGrowthYoY',
      ratio: data.pnl.ratios.salesGrowthYoY,
      format: 'pct',
      drilldownKeys: ['ING'],
    },
  ]

  const handleClick = (card: KpiCardDef, source: 'balance' | 'pnl') => {
    const drilldown =
      source === 'balance' ? data.balance.drilldown : data.pnl.drilldown
    const seen = new Set<string>()
    const accounts: AccountRow[] = []
    for (const key of card.drilldownKeys) {
      const rows = drilldown[key] ?? []
      for (const a of rows) {
        const tag = `${a.code}|${a.amount}`
        if (seen.has(tag)) continue
        seen.add(tag)
        accounts.push(a)
      }
    }
    onDrilldown(`${card.ratio.label} — ${card.drilldownKeys.join(' / ')}`, accounts)
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold t-secondary uppercase tracking-wider">
        {t('ratiosBalance')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {balanceCards.map((c) => (
          <ScoreCard
            key={c.key}
            ratio={c.ratio}
            ratioKey={c.key as RatioKey}
            format={c.format}
            onClick={() => handleClick(c, 'balance')}
          />
        ))}
      </div>

      <h2 className="text-sm font-semibold t-secondary uppercase tracking-wider pt-2">
        {t('ratiosPnl')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {pnlCards.map((c) => (
          <ScoreCard
            key={c.key}
            ratio={c.ratio}
            ratioKey={c.key as RatioKey}
            format={c.format}
            onClick={() => handleClick(c, 'pnl')}
          />
        ))}
      </div>
    </section>
  )
}

/**
 * Tarjeta de ratio al estilo de Ledgeria: además del número y el color, dice en
 * una frase POR QUÉ está así.
 *
 * Antes solo había una barra de color y un `title` del navegador con la fórmula:
 * había que saberse los umbrales y pasar el ratón por encima para entender nada.
 * Ahora el estado se nombra (Óptimo / Precaución / Atención) y debajo va el
 * motivo concreto del valor actual; la fórmula y los umbrales siguen a un paso,
 * en el icono de información.
 */
function ScoreCard({
  ratio,
  ratioKey,
  format,
  onClick,
}: {
  ratio: Ratio
  /** Clave del ratio: elige el texto del porqué. */
  ratioKey: RatioKey
  format: RatioFormat
  onClick: () => void
}) {
  const { t } = useTranslation('accounting')
  const color = STATUS_COLOR[ratio.status]
  const isNa = ratio.status === 'na' || ratio.value === null

  // El porqué se calcula del VALOR, no del semáforo, porque dos valores del
  // mismo color pueden estar así por motivos opuestos: en liquidez, ámbar es
  // "cobertura ajustada" por abajo y "recursos ociosos" por arriba.
  const why = t(`insight.${ratioKey}.${ratioInsightKey(ratioKey, ratio.value)}`, {
    defaultValue: '',
  })

  const detail = isNa
    ? t('tooltip.divByZero')
    : [
        t('tooltip.formula', { value: ratio.formula }),
        t('tooltip.thresholds', { value: ratio.thresholds }),
        ratio.tooltip,
      ].join('\n')

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative text-left rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4 hover:bg-[var(--bg-hover)] transition-colors focus:outline-none focus:ring-2 focus:ring-toga-500/60 flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider t-muted leading-tight">
          {ratio.label}
        </span>
        {/* La fórmula y los umbrales dejan de ocupar sitio, pero siguen a mano. */}
        <span title={detail} className="shrink-0 t-faint hover:t-secondary cursor-help">
          <Info size={13} />
        </span>
      </div>

      <div
        className="text-2xl font-semibold font-mono mt-2"
        style={{ color: isNa ? 'var(--text-muted, #64748b)' : undefined }}
      >
        {formatRatio(ratio.value, format)}
      </div>

      <div className="flex items-center gap-1.5 mt-1.5">
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: color }}
          aria-hidden
        />
        <span className="text-[11px] font-medium" style={{ color }}>
          {t(`insight.status.${ratio.status}`)}
        </span>
      </div>

      {why && <p className="text-[11px] t-secondary leading-snug mt-1.5 line-clamp-3">{why}</p>}
    </button>
  )
}

// ============================================================================
// Sección 2 — Estructura del balance (barras apiladas 100%)
// ============================================================================

interface BalanceVarsLike {
  AC: number
  ANC: number
  PC: number
  PNC: number
  PN: number
}

function BalanceStructureSection({ vars: v }: { vars: BalanceVarsLike }) {
  const { t } = useTranslation('accounting')
  const totalActivo = v.AC + v.ANC
  const totalPasPn = v.PC + v.PNC + v.PN

  const chartData = useMemo(() => {
    return [
      {
        side: t('balance.activo'),
        AC: Math.max(0, v.AC),
        ANC: Math.max(0, v.ANC),
        PC: 0,
        PNC: 0,
        PN: 0,
        total: totalActivo,
      },
      {
        side: t('balance.pasivoPn'),
        AC: 0,
        ANC: 0,
        PC: Math.max(0, v.PC),
        PNC: Math.max(0, v.PNC),
        PN: Math.max(0, v.PN),
        total: totalPasPn,
      },
    ]
  }, [v, totalActivo, totalPasPn, t])

  return (
    <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
      <header className="flex items-center justify-between mb-3">
        <h2 className="font-semibold t-primary">{t('balance.title')}</h2>
        <span className="text-xs t-muted">
          {formatCents(totalActivo)} vs {formatCents(totalPasPn)}
        </span>
      </header>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            stackOffset="expand"
            margin={{ top: 10, right: 16, bottom: 10, left: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
            <XAxis tickLine={false} axisLine={false}
              type="number"
              tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
              stroke="var(--text-muted, #64748b)"
              fontSize={11}
            />
            <YAxis tickLine={false} axisLine={false}
              type="category"
              dataKey="side"
              stroke="var(--text-secondary, #94a3b8)"
              fontSize={12}
            />
            <Tooltip
              content={<BalanceTooltip />}
              cursor={{ fill: 'var(--bg-hover)' }}
            />
            <Bar dataKey="AC" stackId="bal" fill={BAL_AC} radius={[6, 0, 0, 6]}>
              <LabelList content={<PctLabel total="total" labelText="AC" />} />
            </Bar>
            <Bar dataKey="ANC" stackId="bal" fill={BAL_ANC} radius={[0, 6, 6, 0]}>
              <LabelList content={<PctLabel total="total" labelText="ANC" />} />
            </Bar>
            <Bar dataKey="PC" stackId="bal" fill={BAL_PC} radius={[6, 0, 0, 6]}>
              <LabelList content={<PctLabel total="total" labelText="PC" />} />
            </Bar>
            <Bar dataKey="PNC" stackId="bal" fill={BAL_PNC}>
              <LabelList content={<PctLabel total="total" labelText="PNC" />} />
            </Bar>
            <Bar dataKey="PN" stackId="bal" fill={BAL_PN} radius={[0, 6, 6, 0]}>
              <LabelList content={<PctLabel total="total" labelText="PN" />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs t-secondary">
        <LegendDot color={BAL_AC} label={t('balance.ac')} />
        <LegendDot color={BAL_ANC} label={t('balance.anc')} />
        <LegendDot color={BAL_PC} label={t('balance.pc')} />
        <LegendDot color={BAL_PNC} label={t('balance.pnc')} />
        <LegendDot color={BAL_PN} label={t('balance.pn')} />
      </div>
    </section>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-3 rounded-sm"
        style={{ background: color }}
      />
      <span>{label}</span>
    </div>
  )
}

interface BalanceTooltipPayloadItem {
  dataKey: string
  value: number
  color: string
  name: string
  payload: { side: string; total: number }
}

function BalanceTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: BalanceTooltipPayloadItem[]
}) {
  const { t } = useTranslation('accounting')
  if (!active || !payload || payload.length === 0) return null
  const row = payload[0]?.payload
  if (!row) return null
  const total = row.total || 1
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-input)] p-3 text-xs shadow-lg">
      <div className="font-semibold t-primary mb-2">{row.side}</div>
      {payload
        .filter((p) => p.value > 0)
        .map((p) => (
          <div
            key={p.dataKey}
            className="flex items-center justify-between gap-3 t-secondary"
          >
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2 h-2 rounded-sm"
                style={{ background: p.color }}
              />
              {p.dataKey}
            </span>
            <span className="font-mono">
              {formatCents(p.value)} ({((p.value / total) * 100).toFixed(1)}%)
            </span>
          </div>
        ))}
      <div className="mt-2 pt-2 border-t border-[var(--border)] flex justify-between t-primary font-medium">
        <span>{t('total')}</span>
        <span className="font-mono">{formatCents(row.total)}</span>
      </div>
    </div>
  )
}

interface PctLabelProps {
  total: string
  labelText: string
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  // Recharts también pasa el payload entero:
  payload?: { [k: string]: unknown }
}

function PctLabel(props: PctLabelProps) {
  const { x = 0, y = 0, width = 0, height = 0, value = 0, labelText } = props
  if (!value || width < 32) return null
  // Recharts pasa `value` ya en porcentaje fraccional para layout="vertical"
  // con stackOffset="expand" — pero por seguridad reconstruimos desde el ancho.
  const cx = Number(x) + Number(width) / 2
  const cy = Number(y) + Number(height) / 2
  return (
    <text
      x={cx}
      y={cy}
      fill="#fff"
      fontSize={11}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="middle"
      pointerEvents="none"
    >
      {labelText}
    </text>
  )
}

// ============================================================================
// Sección 3 — Waterfall del P&L
// ============================================================================

interface WaterfallChartRow {
  label: string
  base: number
  delta: number
  /** Para mostrar en el tooltip / label. */
  rawValue: number
  cumulative: number
  type: WaterfallStep['type']
  /** Color del segmento visible. */
  color: string
}

function WaterfallSection({ steps }: { steps: WaterfallStep[] }) {
  const { t } = useTranslation('accounting')
  const chartData = useMemo<WaterfallChartRow[]>(() => {
    return steps.map((s) => {
      const previousCumulative = s.cumulative - s.value
      // Para subtotales / start mostramos la barra desde 0 hasta cumulative.
      const isAnchor =
        s.type === 'subtotal' || s.type === 'start' || s.type === 'end'
      const base = isAnchor ? 0 : Math.min(previousCumulative, s.cumulative)
      const delta = isAnchor
        ? Math.max(0, s.cumulative)
        : Math.abs(s.value)
      let color: string
      if (isAnchor) color = WF_SUBTOTAL
      else if (s.type === 'positive') color = WF_POSITIVE
      else color = WF_NEGATIVE
      return {
        label: s.label,
        base,
        delta,
        rawValue: s.value,
        cumulative: s.cumulative,
        type: s.type,
        color,
      }
    })
  }, [steps])

  if (steps.length === 0) {
    return (
      <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <h2 className="font-semibold t-primary mb-3">{t('waterfall.title')}</h2>
        <p className="text-sm t-muted">{t('waterfall.noData')}</p>
      </section>
    )
  }

  return (
    <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
      <h2 className="font-semibold t-primary mb-3">{t('waterfall.title')}</h2>
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 16, bottom: 50, left: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
            <XAxis tickLine={false} axisLine={false}
              dataKey="label"
              stroke="var(--text-muted, #64748b)"
              fontSize={11}
              angle={-25}
              textAnchor="end"
              interval={0}
              height={70}
            />
            <YAxis tickLine={false} axisLine={false}
              stroke="var(--text-muted, #64748b)"
              fontSize={11}
              tickFormatter={(v: number) => formatCents(v)}
            />
            <Tooltip
              content={<WaterfallTooltip />}
              cursor={{ fill: 'var(--bg-hover)' }}
            />
            <Bar dataKey="base" stackId="wf" fill="transparent" />
            <Bar dataKey="delta" stackId="wf" radius={[4, 4, 0, 0]}>
              {chartData.map((row, i) => (
                <Cell key={i} fill={row.color} />
              ))}
              <LabelList
                dataKey="rawValue"
                position="top"
                fontSize={10}
                fill="var(--text-secondary, #94a3b8)"
                formatter={(v: number) => formatCents(v)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs t-secondary">
        <LegendDot color={WF_SUBTOTAL} label={t('waterfall.subtotal')} />
        <LegendDot color={WF_POSITIVE} label={t('waterfall.income')} />
        <LegendDot color={WF_NEGATIVE} label={t('waterfall.expense')} />
      </div>
    </section>
  )
}

interface WaterfallTooltipPayloadItem {
  dataKey: string
  value: number
  payload: WaterfallChartRow
}

function WaterfallTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: WaterfallTooltipPayloadItem[]
}) {
  const { t } = useTranslation('accounting')
  if (!active || !payload || payload.length === 0) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-input)] p-3 text-xs shadow-lg">
      <div className="font-semibold t-primary mb-1">{row.label}</div>
      <div className="t-secondary">
        {t('waterfall.value')} <span className="font-mono">{formatCents(row.rawValue)}</span>
      </div>
      <div className="t-secondary">
        {t('waterfall.cumulative')} <span className="font-mono">{formatCents(row.cumulative)}</span>
      </div>
    </div>
  )
}

// ============================================================================
// Sección 4 — Análisis vertical
// ============================================================================

interface VerticalRow {
  label: string
  amount: number
  pctOfIng: number | null
}

function VerticalAnalysisSection({ rows }: { rows: VerticalRow[] }) {
  const { t } = useTranslation('accounting')
  // Heurística: los conceptos cuyo label aparece como "subtotal" del waterfall.
  const isSubtotal = (label: string): boolean => {
    const lower = label.toLowerCase()
    return (
      lower.startsWith('margen') ||
      lower.startsWith('ebitda') ||
      lower.startsWith('ebit') ||
      lower.startsWith('resultado') ||
      lower.startsWith('beneficio')
    )
  }

  return (
    <section className="rounded-xl border border-[var(--border)] overflow-hidden">
      <div className="px-4 py-3 bg-[var(--bg-input-soft)] border-b border-[var(--border)]">
        <h2 className="font-semibold t-primary">{t('vertical.title')}</h2>
        <p className="text-xs t-muted mt-0.5">{t('vertical.subtitle')}</p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg-input-soft)] t-muted">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t('vertical.concept')}</th>
            <th className="text-right px-4 py-2 font-medium">{t('vertical.amount')}</th>
            <th className="text-right px-4 py-2 font-medium">{t('vertical.pctIncome')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const subtotal = isSubtotal(r.label)
            return (
              <tr
                key={i}
                className={[
                  'border-t border-[var(--border)]/60',
                  subtotal ? 'bg-[var(--bg-hover)]' : '',
                ].join(' ')}
              >
                <td
                  className={[
                    'px-4 py-2',
                    subtotal ? 'font-semibold t-primary' : 't-secondary',
                  ].join(' ')}
                >
                  {r.label}
                </td>
                <td
                  className={[
                    'px-4 py-2 text-right font-mono',
                    subtotal ? 'font-semibold t-primary' : 't-secondary',
                  ].join(' ')}
                >
                  {formatCents(r.amount)}
                </td>
                <td
                  className={[
                    'px-4 py-2 text-right font-mono',
                    subtotal ? 'font-semibold t-primary' : 't-secondary',
                  ].join(' ')}
                >
                  {r.pctOfIng === null ? (
                    <span
                      title={t('vertical.noIncomeTitle')}
                      className="t-muted"
                    >
                      —
                    </span>
                  ) : (
                    `${r.pctOfIng.toFixed(1)}%`
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}

// ============================================================================
// Comparativa anual
// ============================================================================

interface YearRow {
  year: number
  ing: number
  ebitda: number
  net: number
  at: number
  pn: number
}

/**
 * Evolución de los últimos ejercicios, la tercera pestaña de Ledgeria.
 *
 * NO añade endpoint: pide el MISMO `/accounting/kpis` una vez por año, en
 * paralelo. Es una llamada por ejercicio, pero la pantalla es de ADMIN y se
 * consulta de tarde en tarde; a cambio, las cifras salen del cálculo ya
 * verificado en vez de una segunda implementación que podría discrepar del
 * cuadro de mando — que es exactamente el bug de coherencia que ya costó
 * arreglar tres veces en esta contabilidad.
 *
 * Los años sin movimientos se descartan: una fila a cero no dice nada y
 * aplasta la escala del gráfico.
 */
function YearComparisonSection({ years }: { years: number[] }) {
  const { t } = useTranslation('accounting')
  const [rows, setRows] = useState<YearRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Ejercicios cuyo cálculo falló: se omiten del gráfico, pero se dicen. */
  const [failedYears, setFailedYears] = useState<number[]>([])

  useEffect(() => {
    const ctrl = new AbortController()
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const asc = [...years].sort((a, b) => a - b)
        const results = await Promise.all(
          asc.map(async (y) => {
            try {
              const r = await api.get<FinancialKpisResponse>(
                `/accounting/kpis?year=${y}&period=YEAR`
              )
              return { year: y, r }
            } catch {
              // Un ejercicio que falla no puede tumbar la comparativa entera...
              // pero tampoco puede desaparecer sin decir nada: una comparativa
              // a la que le falta un año, y que no avisa, se lee como si ese
              // año no hubiera existido.
              return { year: y, r: null }
            }
          })
        )
        if (ctrl.signal.aborted) return
        setFailedYears(results.filter((x) => x.r === null).map((x) => x.year))
        const mapped = results
          .filter((x): x is { year: number; r: FinancialKpisResponse } => x.r !== null)
          .map(({ year, r }) => ({
            year,
            ing: r.pnl.vars.ING,
            ebitda: r.pnl.vars.ebitda,
            net: r.pnl.vars.beneficioNeto,
            at: r.balance.vars.AT,
            pn: r.balance.vars.PN,
          }))
          .filter((r) => r.ing !== 0 || r.at !== 0)
        setRows(mapped)
      } catch (err) {
        if (ctrl.signal.aborted) return
        setError(err instanceof Error ? err.message : t('errorLoad'))
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    })()
    return () => ctrl.abort()
  }, [years, t])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm t-muted">
        <Loader2 size={16} className="animate-spin" />
        {t('loading')}
      </div>
    )
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        <AlertCircle size={16} />
        <span>{error}</span>
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return <p className="text-sm t-muted">{t('comparison.empty')}</p>
  }

  const avisoAnosFallidos = failedYears.length > 0 && (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <span>{t('comparison.failedYears', { years: failedYears.join(', ') })}</span>
    </div>
  )

  return (
    <section className="space-y-4">
      {avisoAnosFallidos}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <h2 className="text-sm font-semibold t-primary mb-3">{t('comparison.chartTitle')}</h2>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={4}>
            <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.4} />
            <XAxis dataKey="year" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              width={72}
              tickFormatter={(v: number) => formatCents(v)}
            />
            <Tooltip
              cursor={{ fill: 'var(--bg-hover)' }}
              formatter={(v: number) => formatCents(v)}
              contentStyle={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                fontSize: 12,
              }}
            />
            <Bar dataKey="ing" name={t('comparison.income')} fill="#0ea5e9" radius={[6, 6, 0, 0]} />
            <Bar dataKey="ebitda" name="EBITDA" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
            <Bar dataKey="net" name={t('comparison.net')} fill="#16a34a" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap gap-4 mt-2">
          <LegendDot color="#0ea5e9" label={t('comparison.income')} />
          <LegendDot color="#8b5cf6" label="EBITDA" />
          <LegendDot color="#16a34a" label={t('comparison.net')} />
        </div>
      </div>

      <div className="rounded-xl border border-[var(--border)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              <th className="px-3 py-2 text-left">{t('comparison.year')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.income')}</th>
              <th className="px-3 py-2 text-right">EBITDA</th>
              <th className="px-3 py-2 text-right">{t('comparison.net')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.assets')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.equity')}</th>
              <th className="px-3 py-2 text-right">{t('comparison.growth')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const prev = i > 0 ? rows[i - 1]! : null
              // Sin año anterior, o con ingresos previos a cero, el crecimiento
              // no significa nada: se deja en blanco en vez de inventar un %.
              const growth =
                prev && prev.ing !== 0 ? ((r.ing - prev.ing) / Math.abs(prev.ing)) * 100 : null
              return (
                <tr key={r.year} className="border-t border-base/40">
                  <td className="px-3 py-2 font-mono t-primary">{r.year}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCents(r.ing)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCents(r.ebitda)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCents(r.net)}</td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">{formatCents(r.at)}</td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">{formatCents(r.pn)}</td>
                  <td
                    className="px-3 py-2 text-right font-mono"
                    style={{ color: growth === null ? undefined : growth >= 0 ? '#16a34a' : '#dc2626' }}
                  >
                    {growth === null ? '—' : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
